import {
  DeleteOutlined,
  EditOutlined,
  LoadingOutlined,
  WarningFilled
} from "@ant-design/icons"
import { Button, Flex, Popconfirm, Space, Tag, Tooltip, Typography } from "antd"
import { type ColumnsType } from "antd/es/table"
import dayjs from "dayjs"
import React from "react"
import type { TFunction } from "react-i18next"

import { empty } from "../../../../../../../constants/placeholders"
import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"
import {
  type Attachment,
  AttachmentModels,
  AttachmentParentModels
} from "../../../../../../Album/types/attachment.types.ts"
import { ALL_DATE_FORMATS } from "../../../../../../TeamManagementPage/constants/date-formats.ts"
import { AttachmentsController } from "../../../../../components/AttachmentsController.tsx"
import type { ProjectMilestoneFinanceItem } from "../../../../../types/project-phases.types.tsx"
import {
  FINANCE_ITEM_STATUSES_MAP,
  FINANCE_ITEM_TYPES_MAP,
  isFinanceItemLocked,
  type FinanceItemStatuses
} from "../../../../constants/finance-item-constants.ts"
import { StatusTag } from "../../../StatusTag.tsx"

const { Text, Paragraph } = Typography

interface GetColumnsArgs {
  t: TFunction
  currentUserId: string
  editingId: string | null
  isAdding: boolean
  projectId: string
  deletingId: string | null
  disabled: boolean
  //
  editAllowed?: boolean
  deleteAllowed?: boolean
  uploadFileAllowed?: boolean
  deleteFileAllowed?: boolean
  deleteForeignFileAllowed?: boolean
  //
  onEdit: (record: ProjectMilestoneFinanceItem) => void
  onDelete: (id: string) => void
}

export const getColumns = ({
  t,
  currentUserId,
  editingId,
  isAdding,
  projectId,
  deletingId,
  disabled,
  //
  editAllowed,
  deleteAllowed,
  uploadFileAllowed,
  deleteFileAllowed,
  deleteForeignFileAllowed,
  //
  onDelete,
  onEdit
}: GetColumnsArgs): ColumnsType<ProjectMilestoneFinanceItem> => [
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    sorter: (a, b) => dayjs(a.date).diff(dayjs(b.date)),
    width: 135,
    render: (value: string, record) => {
      const parsedDate = dayjs(record?.date, ALL_DATE_FORMATS, true)
      const parsedDue = dayjs(record?.due_date, ALL_DATE_FORMATS, true)
      const backDated =
        parsedDate.isValid() &&
        parsedDue.isValid() &&
        parsedDate.isAfter(parsedDue, "day")
      return (
        <Space>
          <Text>{value || empty}</Text>
          {backDated && (
            <Tooltip title="Date is after due date">
              <WarningFilled className="text-warning" />
            </Tooltip>
          )}
        </Space>
      )
    }
  },
  {
    title: "Financial Item",
    dataIndex: "name",
    key: "name",
    width: 180,
    render: (text: string) => <Text ellipsis={{ tooltip: text }}>{text}</Text>
  },
  {
    title: "Type",
    dataIndex: "item_type_display",
    key: "item_type_display",
    width: 135,
    render: (type: string, record) => {
      const config = FINANCE_ITEM_TYPES_MAP?.[record?.item_type]

      return (
        <Tag style={config?.style} className="m-0">
          {type}
        </Tag>
      )
    }
  },
  {
    title: "Due Date",
    dataIndex: "due_date",
    key: "due_date",
    width: 135,
    sorter: (a, b) =>
      dayjs(a.due_date, ALL_DATE_FORMATS).diff(
        dayjs(b.due_date, ALL_DATE_FORMATS)
      ),
    render: (value: string) => {
      const parsedDate = dayjs(value, ALL_DATE_FORMATS, true)
      const isOverdue = parsedDate && parsedDate.isBefore(dayjs().tz(), "day")

      return (
        <Space>
          <Text>{value || empty}</Text>
          {isOverdue && (
            <Tooltip title={"Overdue"}>
              <WarningFilled className={"text-warning"} />
            </Tooltip>
          )}
        </Space>
      )
    }
  },
  {
    title: "Comments",
    dataIndex: "comment",
    key: "comment",
    width: 200,
    onCell: () => ({ style: { maxWidth: 200 } }),
    render: (description: string) => (
      <Paragraph
        className={"mb-0"}
        ellipsis={{ rows: 3, tooltip: description }}
      >
        {description || empty}
      </Paragraph>
    )
  },
  {
    title: "Value USD",
    dataIndex: "value",
    key: "value",
    width: 120,
    render: (value: number | string, record: ProjectMilestoneFinanceItem) => {
      const planned = typeof value === "string" ? parseFloat(value) : value
      const paid = parseFloat(record.exercised_value ?? "0")
      const hasPayments = (record.partial_finances?.length ?? 0) > 0
      if (hasPayments && Number.isFinite(paid) && paid !== 0) {
        if (record.is_overpaid) {
          return (
            <Tooltip title={`Over-paid by ${budgetFormat(paid - planned)}`}>
              <Text className="text-red-500 font-bold">
                {`${budgetFormat(paid)} / ${budgetFormat(planned)}`}
              </Text>
            </Tooltip>
          )
        }
        return <Text>{`${budgetFormat(paid)} / ${budgetFormat(planned)}`}</Text>
      }
      return <Text>{budgetFormat(planned)}</Text>
    }
  },
  {
    title: "Source/Target",
    dataIndex: "responsible_type_display",
    key: "responsible_type_display",
    width: 150,
    render: (value: string) => value || empty
  },
  {
    //todo ????
    title: "Name",
    dataIndex: "custom_subcontractor_name",
    key: "custom_subcontractor_name",
    width: 250,
    ellipsis: true,
    render: (customName, record) => {
      const name =
        customName || record?.subcontractor_name || record?.assignee_name

      if (!name && record?.subcontractor) {
        return "Unnamed"
      }

      return name || empty
    }
  },
  {
    title: t("lineStatus.header"),
    dataIndex: "status",
    key: "status",
    width: 140,
    render: (status: FinanceItemStatuses, record) => {
      const statusData = FINANCE_ITEM_STATUSES_MAP?.[status]

      return (
        <StatusTag
          style={statusData?.style}
          tooltipKey={statusData?.tooltipKey}
          label={record?.status_display}
          className="m-0"
        />
      )
    }
  },
  {
    title: "Attachments",
    key: "attachments",
    dataIndex: "attachments",
    width: 100,
    align: "center",
    render: (attachments: Attachment[], record) => (
      <AttachmentsController
        currentUserId={currentUserId}
        addAllowed={editAllowed && !isFinanceItemLocked(record?.status)}
        deleteAllowed={deleteFileAllowed}
        deleteForeignAllowed={deleteForeignFileAllowed}
        project_id={projectId}
        model_type={AttachmentModels.FINANCIAL_ITEM}
        parent_type={AttachmentParentModels.MILESTONE}
        object_id={record?.id}
        attachments={attachments}
      />
    )
  },
  {
    title: "Actions",
    key: "actions",
    width: 50,
    align: "center",
    fixed: "right",
    hidden: !deleteAllowed && !editAllowed && !isAdding,
    render: (_, record) => {
      const isDeleting = deletingId === record.id

      return (
        <Flex gap="small" justify="center">
          {editAllowed && (
            <Button
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
              disabled={!!editingId || isAdding || disabled || isDeleting}
            />
          )}
          {deleteAllowed && (
            <Popconfirm
              title="Delete this item?"
              onConfirm={() => onDelete(record.id)}
              okText="Yes"
              cancelText="No"
              disabled={isDeleting}
            >
              <Button
                danger
                icon={isDeleting ? <LoadingOutlined /> : <DeleteOutlined />}
                disabled={!!editingId || isAdding || disabled || isDeleting}
              />
            </Popconfirm>
          )}
        </Flex>
      )
    }
  }
]
